import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Button } from '@m/alchemy-ui/Button';
import { BarChart } from '@m/alchemy-ui/BarChart';
import { EmptyState as ErrorMessage } from '@m/alchemy-ui/esm/empty-state/EmptyState';
import { LoadingIndicator } from '@m/alchemy-ui/LoadingIndicator';
import { Select } from '@m/alchemy-ui/Select';
import { useIntl } from 'react-intl';
import { ThemeProvider } from '@m/alchemy-ui/theme';
import {
  type ChallengesQuery,
  type CoauthorIdeasQuery,
  type FilteredUsersQuery,
  GET_CHALLENGES,
  GET_COAUTHOR_IDEAS,
  GET_COUNTRIES,
  GET_TAG_IDEAS_AND_COUNT,
  GET_USERFIELDS_FILTERED_USERIDS,
  type GetCountriesQuery,
  type GetIdeaTagsSummaryQuery,
  type GetTagIdeasAndCountQuery
} from '../../graphql/queries';
import { useUserFields, useWindowWidth } from '../../hooks';
import { CommunityContext } from '../../context';
import { GET_IDEA_TAGS_SUMMARY } from '../../graphql/queries/stat';
import { decodeHtml } from '../../helpers';
import {
  ChartActionsRow,
  ChartScroll,
  ChartScrollInner,
  Grid,
  GridCol,
  GridHeader,
  GridRow,
  Pagination
} from './Analytics.styled';
import * as Styled from './components/SearchFilters.styled';
import { CustomFilters } from './CustomFilters';
import { type IdeaTagSummaryItemType, type UserFieldType } from './types';
import { ChartExportMenu, IdeasTable, TableView } from './components';
import { IDEAS_DEFAULT_OFFSET, IDEAS_DEFAULT_SORT, IDEAS_PER_PAGE } from './constants';
import { AlchemySortDirectionType, ChallengeSelectionType, CoreApiSortDirectionType } from './enums';
import { accessibleChartTheme, getChartMinWidth, getChartScrollWidth } from './helpers/chartUtils';

export const IdeasByTagStats = ({ dataTableViewEnabled }: { dataTableViewEnabled: boolean }) => {
  const intl = useIntl();
  const community = useContext(CommunityContext);
  const { data: communityUserFields } = useUserFields();

  const [tag, setTag] = useState<string | undefined>();

  const defaultUserFieldOptions: UserFieldType = {};
  const [userFieldOptions, setUserFieldOptions] = useState(defaultUserFieldOptions);

  const [countries, setCountries] = useState<string[]>([]);
  const [challenge, setChallenge] = useState<string>(ChallengeSelectionType.PUBLISHED);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const { data: countriesList } = useQuery<GetCountriesQuery>(GET_COUNTRIES, {
    variables: { id: community.id },
    fetchPolicy: 'cache-first'
  });

  const ideasLabel = intl.formatMessage({ id: 'ideas', defaultMessage: 'Ideas' });

  const windowWidth = useWindowWidth();
  const chartScrollWidth = useMemo(() => getChartScrollWidth(windowWidth), [windowWidth]);

  useEffect(() => {
    countriesList?.community && setCountries(countriesList.community?.countries as string[]);
  }, [countriesList]);

  const [tagIdeasQueryVariables, setTagIdeasQueryVariables] = useState({
    challengeSelection: challenge,
    tagSelection: tag,
    filters: { userFields: JSON.stringify(userFieldOptions) },
    order: [IDEAS_DEFAULT_SORT],
    offset: IDEAS_DEFAULT_OFFSET
  });

  const isTagSelected = useMemo(() => tagIdeasQueryVariables.tagSelection !== undefined, [tagIdeasQueryVariables]);

  const { data: ideasData, loading: loadingIdeas } = useQuery<GetTagIdeasAndCountQuery>(GET_TAG_IDEAS_AND_COUNT, {
    variables: tagIdeasQueryVariables,
    // If a specific tag is not selected, do not request any ideas data
    skip: !isTagSelected
  });

  const { data: customFiltersUserIds } = useQuery<FilteredUsersQuery>(GET_USERFIELDS_FILTERED_USERIDS, {
    variables: { filters: { userFields: JSON.stringify(userFieldOptions) } },
    fetchPolicy: 'cache-first'
  });

  const { data: coauthorIdeaIds } = useQuery<CoauthorIdeasQuery>(GET_COAUTHOR_IDEAS, {
    variables: {
      customFiltersUserIds: customFiltersUserIds?.filteredUsers || [],
      filters: { userFields: JSON.stringify(userFieldOptions) }
    },
    // There is no need to request coauthors data in case custom fields are not selected
    skip: !customFiltersUserIds
  });

  const [ideaTagsSummaryQueryVariables, setIdeaTagsSummaryQueryVariables] = useState({
    challengeSelection: challenge,
    filters: { userFields: JSON.stringify(userFieldOptions) }
  });

  const { data: chartData, loading: loadingChart } = useQuery<GetIdeaTagsSummaryQuery>(GET_IDEA_TAGS_SUMMARY, {
    variables: ideaTagsSummaryQueryVariables
  });

  // Used for getting the challenges dropdown
  const getChallengesVariables = { order: ['title'] };
  const { loading: loadingChallenges, data: challengeList } = useQuery<ChallengesQuery>(GET_CHALLENGES, {
    variables: getChallengesVariables,
    fetchPolicy: 'cache-first'
  });

  useEffect(() => {
    setTagIdeasQueryVariables({
      challengeSelection: challenge,
      tagSelection: tag,
      filters: { userFields: JSON.stringify(userFieldOptions) },
      order: [IDEAS_DEFAULT_SORT],
      offset: IDEAS_DEFAULT_OFFSET
    });
  }, [challenge, setTagIdeasQueryVariables, tag, userFieldOptions]);

  const sortIdeas = (column: string) => {
    const { order } = tagIdeasQueryVariables;
    const [sortColumn, sortDirection] = order[0];
    const newOrder = [
      [
        column,
        column === sortColumn && sortDirection === CoreApiSortDirectionType.ASC
          ? CoreApiSortDirectionType.DESC
          : CoreApiSortDirectionType.ASC
      ]
    ];

    setTagIdeasQueryVariables({
      ...tagIdeasQueryVariables,
      order: newOrder
    });
  };

  const getColumnSortDirection = (column: string) => {
    const { order } = tagIdeasQueryVariables;
    const [sortColumn, sortDirection] = order[0];
    return sortColumn === column
      ? sortDirection === CoreApiSortDirectionType.DESC
        ? AlchemySortDirectionType.DESC
        : AlchemySortDirectionType.ASC
      : 'none';
  };

  const tagSummary = useMemo(() => chartData?.ideaTagsSummary ?? [], [chartData?.ideaTagsSummary]);

  const chartSeries = useMemo(
    () => [
      {
        id: ideasLabel,
        data: tagSummary.map(({ ideaCount, tagId, tagName }: IdeaTagSummaryItemType) => ({
          x: decodeHtml(tagName),
          y: ideaCount ?? 0,
          tagId
        }))
      }
    ],
    [ideasLabel, tagSummary]
  );

  const chartMinWidth = useMemo(() => getChartMinWidth(chartSeries.length), [chartSeries.length]);

  const formatDate = (date: Date) => date.toISOString().slice(0, 10);

  const tableColumns = useMemo(
    () => [
      { label: intl.formatMessage({ id: 'tag', defaultMessage: 'Tag' }) },
      { label: intl.formatMessage({ id: 'ideas', defaultMessage: 'Ideas' }) }
    ],
    [intl]
  );

  const buildTableRows = useCallback(() => {
    return tagSummary.map((item) => [decodeHtml(item.tagName), item.ideaCount]);
  }, [tagSummary]);

  const tableRows = useMemo(() => {
    if (!dataTableViewEnabled) {
      return [];
    }

    return buildTableRows();
  }, [buildTableRows, dataTableViewEnabled]);

  const getExportData = useCallback(() => {
    return { columns: tableColumns, rows: buildTableRows() };
  }, [buildTableRows, tableColumns]);

  if ((loadingChallenges && !challengeList) || (loadingChart && !chartData)) {
    return (
      <Styled.LoadingContainer>
        <LoadingIndicator inline />
      </Styled.LoadingContainer>
    );
  }

  const handleTagSelection = (tagId?: string) => {
    if (tagId) {
      setTag(tagId);
    }
  };

  return (
    <Grid key="ideasByTagStats">
      <CustomFilters
        userFieldOptions={userFieldOptions}
        communityUserFields={communityUserFields}
        countries={countries}
        userFieldUpdate={setUserFieldOptions}
      />
      <GridHeader key="challengeSelectionHeader">
        {intl.formatMessage({ id: 'show', defaultMessage: 'Show' })}
      </GridHeader>
      <GridRow key="challengeSelection">
        <Select
          defaultValue={challenge}
          options={[
            {
              label: intl.formatMessage({ id: 'allPublishedChallenges', defaultMessage: 'All Published Challenges' }),
              value: ChallengeSelectionType.PUBLISHED,
              'data-testid': `challenge-${ChallengeSelectionType.PUBLISHED}`
            },
            {
              label: intl.formatMessage({ id: 'allChallenges', defaultMessage: 'All Challenges' }),
              value: ChallengeSelectionType.ALL,
              'data-testid': `challenge-${ChallengeSelectionType.ALL}`
            },
            ...((challengeList?.challenges || [])?.map((c) => ({
              label: c.title,
              value: c.id,
              'data-testid': `challenge-${c.title}`
            })) || {})
          ]}
          onChange={(challengeSelection) => {
            setChallenge(challengeSelection.value);
            setTag(undefined);
          }}
        />
      </GridRow>
      <GridRow key="updateButton">
        <Button
          priority="secondary"
          onClick={() => {
            setIdeaTagsSummaryQueryVariables({
              challengeSelection: challenge,
              filters: { userFields: JSON.stringify(userFieldOptions) }
            });
          }}
        >
          {intl.formatMessage({ id: 'update', defaultMessage: 'Update' })}
        </Button>
      </GridRow>
      {!dataTableViewEnabled && (
        <ChartActionsRow key="ideasByTagExport">
          <ChartExportMenu
            chartRef={chartContainerRef}
            fileName={intl.formatMessage({ id: 'ideasByTag', defaultMessage: 'Ideas by Tag' })}
            formatDate={formatDate}
            getExportData={getExportData}
          />
        </ChartActionsRow>
      )}
      <GridRow key="chart">
        {dataTableViewEnabled ? (
          <TableView
            columns={tableColumns}
            rows={tableRows}
            formatDate={formatDate}
            onRowClick={(index: number) => {
              const tagId = tagSummary[index]?.tagId;
              handleTagSelection(tagId === undefined ? undefined : String(tagId));
            }}
            summary={intl.formatMessage({ id: 'ideasByTag', defaultMessage: 'Ideas by Tag' })}
          />
        ) : (
          <ChartScroll width={chartScrollWidth}>
            <ChartScrollInner minWidth={chartMinWidth} ref={chartContainerRef}>
              <ThemeProvider theme={accessibleChartTheme}>
                <BarChart
                  axisX={{ label: intl.formatMessage({ id: 'tag', defaultMessage: 'Tag' }) }}
                  axisY={{
                    formatScreenReaderValue: (value) => {
                      return `${ideasLabel}: ${value}`;
                    },
                    label: intl.formatMessage({
                      id: 'numberOfIdeasWithThisTag',
                      defaultMessage: 'Number of ideas with this tag'
                    }),
                    formatValue: (value) => {
                      const numericValue = Number(value);
                      return Number.isInteger(numericValue) ? numericValue : '';
                    }
                  }}
                  data={chartSeries}
                  domainScaling="from-zero"
                  hasLegend={false}
                  hasValueLabels
                  onDataClick={(dataPoint) => {
                    if (dataPoint.tagId !== undefined) {
                      handleTagSelection(String(dataPoint.tagId));
                    }
                  }}
                />
              </ThemeProvider>
            </ChartScrollInner>
          </ChartScroll>
        )}
      </GridRow>
      {isTagSelected && (
        <GridRow key="table" justify="center">
          {loadingIdeas && !ideasData ? (
            <Styled.LoadingContainer>
              <LoadingIndicator inline />
            </Styled.LoadingContainer>
          ) : ideasData?.tagIdeasAndCount && ideasData.tagIdeasAndCount.count > 0 ? (
            <>
              <GridCol>
                <IdeasTable
                  getColumnSortDirection={getColumnSortDirection}
                  ideasAndCount={ideasData?.tagIdeasAndCount || { count: 0, rows: [] }}
                  intl={intl}
                  sortIdeas={sortIdeas}
                />
              </GridCol>
              <GridCol>
                <Pagination
                  numPages={Math.ceil(ideasData.tagIdeasAndCount.count / IDEAS_PER_PAGE)}
                  activeIndex={Math.ceil(tagIdeasQueryVariables.offset / IDEAS_PER_PAGE)}
                  onPageClick={(page) => {
                    const pageIndex = page.index as number;
                    setTagIdeasQueryVariables({
                      ...tagIdeasQueryVariables,
                      offset: pageIndex * IDEAS_PER_PAGE
                    });
                  }}
                />
              </GridCol>
            </>
          ) : (
            <ErrorMessage
              message={intl.formatMessage({ id: 'noIdeasWereFound', defaultMessage: 'No ideas were found' })}
              details={intl.formatMessage({
                id: 'pleaseAdjustYourSearch',
                defaultMessage: 'Please adjust your search parameters and try again.'
              })}
            />
          )}
        </GridRow>
      )}
      {isTagSelected && ideasData?.tagIdeasAndCount && ideasData.tagIdeasAndCount.count > 0 && (
        <GridRow key="exportData" justify="start">
          <GridCol>
            <Button
              priority="secondary"
              onClick={() => {
                const challengeId =
                  challenge === ChallengeSelectionType.PUBLISHED || challenge === ChallengeSelectionType.ALL
                    ? '0'
                    : challenge;

                const exportUrl = new URL('/category/admin_challengecsv/' + challengeId, window.location.origin);

                exportUrl.searchParams.append(
                  'includeUnpublished',
                  challenge === ChallengeSelectionType.PUBLISHED ? '0' : '1'
                );

                exportUrl.searchParams.append('includeComments', '0');

                tag && exportUrl.searchParams.append('tagId', tag);

                if (Object.keys(userFieldOptions).length > 0) {
                  exportUrl.searchParams.append(
                    'customFiltersUserIds',
                    (customFiltersUserIds || { filteredUsers: [] }).filteredUsers.toString()
                  );

                  exportUrl.searchParams.append('ideaIds', (coauthorIdeaIds?.coauthorIdeas || []).toString());
                }

                window.location = exportUrl as unknown as Location;
              }}
            >
              {intl.formatMessage({ id: 'exportData', defaultMessage: 'Export Data' })}
            </Button>
          </GridCol>
        </GridRow>
      )}
    </Grid>
  );
};
